import { Link, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { LayoutDashboard, FileText, Settings, LogOut, Menu, X, Bell, Shield, ArrowLeft } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { useAdmin } from "@/hooks/useAdmin";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { useState, useEffect } from "react";
import { cn } from "@/lib/utils";


interface NavItemProps {
  to: string;
  icon: React.ReactNode;
  label: string;
  isActive: boolean;
  colorClass: string;
  hoverClass: string;
  onClick?: () => void;
}

const NavItem = ({ to, icon, label, isActive, colorClass, hoverClass, onClick }: NavItemProps) => (
  <Link
    to={to}
    onClick={onClick}
    className={cn(
      "flex items-center gap-3 px-4 py-3 rounded-lg transition-colors",
      isActive
        ? cn(colorClass, "text-black border-2 border-black shadow-[4px_4px_0_#000] translate-x-[-2px] translate-y-[-2px]")
        : cn(hoverClass, "text-foreground transition-all hover:translate-x-1")
    )}
  >
    {icon}
    <span className="font-medium">{label}</span>
  </Link>
);

export function DashboardSidebar() {
  const { t } = useTranslation();
  const location = useLocation();
  const { user, signOut } = useAuth();
  const { admin, loading: adminLoading } = useAdmin();
  const [isOpen, setIsOpen] = useState(false);

  const navItems = [
    {
      to: "/dashboard",
      icon: <LayoutDashboard className="h-5 w-5" />,
      label: t("dashboard.nav.dashboard"),
      colorClass: "bg-[#FFD93D]", // Yellow
      hoverClass: "hover:bg-[#FFD93D]/20",
    },
    {
      to: "/scripts", // Fixed path to separate it from dashboard
      icon: <FileText className="h-5 w-5" />,
      label: t("dashboard.nav.scripts"),
      colorClass: "bg-[#FF6B9D]", // Pink
      hoverClass: "hover:bg-[#FF6B9D]/20",
    },
    {
      to: "/reminders",
      icon: <Bell className="h-5 w-5" />,
      label: t("dashboard.nav.reminders"),
      colorClass: "bg-[#6FEDD6]", // Cyan
      hoverClass: "hover:bg-[#6FEDD6]/20",
    },
    {
      to: "/settings/profile",
      icon: <Settings className="h-5 w-5" />,
      label: t("dashboard.nav.settings"),
      colorClass: "bg-[#4DA6FF]", // Blue
      hoverClass: "hover:bg-[#4DA6FF]/20",
    },
  ];

  const handleSignOut = async () => {
    await signOut();
  };

  const SidebarContent = ({ onItemClick }: { onItemClick?: () => void }) => (
    <div className="flex flex-col h-full">
      {/* Logo */}
      <div className="p-6">
        <Link to="/" className="logo text-foreground">
          Vesper
        </Link>
      </div>

      {/* Navigation */}
      <nav className="flex-1 px-4 space-y-2">
        {navItems.map((item) => (
          <NavItem
            key={item.to}
            to={item.to}
            icon={item.icon}
            label={item.label}
            isActive={location.pathname === item.to}
            colorClass={item.colorClass}
            hoverClass={item.hoverClass}
            onClick={onItemClick}
          />
        ))}
      </nav>

      {/* User info & Logout */}
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-3 mb-4">
          <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center text-primary-foreground font-semibold">
            {user?.email?.charAt(0).toUpperCase() || "U"}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium truncate">{user?.email}</p>
          </div>
        </div>
        
        <div className="space-y-2">
          {/* Exit to Home */}
          <Link
            to="/"
            onClick={onItemClick}
            className="flex items-center justify-center gap-2 w-full h-11 rounded-lg text-sm font-bold bg-white text-black border-2 border-black transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0_#000]"
          >
            <ArrowLeft className="h-4 w-4" />
            <span>{t("dashboard.nav.exitToHome")}</span>
          </Link>

          {/* Admin Dashboard Link - only visible for active administrators */}
          {admin?.is_active && (
            <Link
              to="/admin"
              onClick={onItemClick}
              className="flex items-center justify-center gap-2 w-full h-11 rounded-lg text-sm font-bold bg-[#1A1A1A] text-white border-2 border-black transition-all hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0_#000]"
            >
              <Shield className="h-4 w-4" />
              <span>{t("dashboard.nav.adminPanel")}</span>
            </Link>
          )}
          
          <button
            onClick={handleSignOut}
            className="flex items-center justify-center gap-2 w-full h-11 rounded-lg text-sm font-bold bg-white text-black border-2 border-black transition-all hover:bg-gray-100 hover:translate-x-[2px] hover:translate-y-[2px] hover:shadow-[4px_4px_0_#000]"
          >
            <LogOut className="h-4 w-4" />
            {t("header.logout")}
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <>
      {/* Mobile Menu Button */}
      <div className="lg:hidden fixed top-4 left-4 z-50">
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetTrigger asChild>
            <Button variant="outline" size="icon">
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>
          <SheetContent side="left" className="w-72 p-0">
            <SheetTitle className="sr-only">Dashboard navigation</SheetTitle>
            <SheetDescription className="sr-only">
              Primary navigation links and account actions
            </SheetDescription>
            <SidebarContent onItemClick={() => setIsOpen(false)} />
          </SheetContent>
        </Sheet>
      </div>

      {/* Desktop Sidebar */}
      <aside className="hidden lg:flex lg:w-64 lg:flex-col lg:fixed lg:inset-y-0 bg-card border-r-2 border-foreground">
        <SidebarContent />
      </aside>
    </>
  );
}
